import { useState, useContext } from 'react';
import { Link, Box, Tooltip, Chip } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SchoolIcon from '@mui/icons-material/School';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AppContext, SttButtonFab, SttPage } from '../../../sporttia/all';
import SttCachedTable from '../../../sporttia/SttCachedTable';
import Teacher from './Teacher';
import { SPORTCENTER_ACTIVITY_TEACHERS } from '../../../lib/queryKeys';
import useScsService from '../../../services/ScsService';
import { SttTopFilteringControls } from '../../../sporttia/SttTopFilteringControls';
import PhotoViewerDialog from '../../../components/dialogs/PhotoViewerDialog';
import { PERMISSIONS } from '../../../lib/permissions';

type FilterState = {
    keyword?: string;
    name?: string;
    email?: string;
    dni?: string;
    trash?: boolean;
};

type TeacherRowData = {
    id: number;
    fullName?: string;
    login?: string;
    email?: string | null;
    phone?: string | null;
    dni?: string;
    img?: { url?: string };
    numClasses?: number;
};

type TeacherIdAndName = {
    id: string | number;
    name: string;
};

export default function Teachers(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const scsService = useScsService();
    const [filters, setFilters] = useState<FilterState>({
        keyword: '',
        trash: false,
    });
    const queryClient = useQueryClient();
    const [teacherIdAndName, setTeacherIdAndName] =
        useState<TeacherIdAndName | null>(null);
    const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
    const [selectedPhotoUrl, setSelectedPhotoUrl] = useState<string | null>(
        null,
    );

    /**
     * Go to create
     */
    const goCreateNew = (): void => {
        setTeacherIdAndName({ name: t('New'), id: 'create' });
    };

    /**
     * Handle avatar click to open photo viewer
     */
    const handleAvatarClick = (row: TeacherRowData) => {
        if (row.img?.url) {
            setSelectedPhotoUrl(row.img.url);
            setPhotoViewerOpen(true);
        }
    };

    /**
     * Handle filtering
     */
    const handleFilter = (updatedFilters: FilterState) => {
        setFilters(updatedFilters);
    };

    // -----| Render |-----
    return (
        <>
            <SttPage title={t('Teachers')}>
                <SttTopFilteringControls
                    p={2}
                    fields={[
                        {
                            caption: t('Search'),
                            name: 'keyword',
                            type: 'text',
                            value: filters.keyword || '',
                            xs: 12,
                            sm: 6,
                            md: 4,
                            lg: 3,
                        },
                    ]}
                    extraFields={[
                        {
                            caption: t('Name'),
                            name: 'name',
                            type: 'text',
                            value: filters.name || '',
                            xs: 12,
                            sm: 6,
                            md: 4,
                        },
                        {
                            caption: t('Email'),
                            name: 'email',
                            type: 'text',
                            value: filters.email || '',
                            xs: 12,
                            sm: 6,
                            md: 4,
                        },
                        {
                            caption: t('DNI'),
                            name: 'dni',
                            type: 'text',
                            value: filters.dni || '',
                            xs: 12,
                            sm: 6,
                            md: 4,
                        },
                    ]}
                    trashAction
                    onFilter={handleFilter}
                />

                <SttCachedTable
                    queryKey={SPORTCENTER_ACTIVITY_TEACHERS}
                    queryFn={(params: any) =>
                        scsService.getTeachers(Number(cxt?.sc?.id) || 0, params)
                    }
                    queryParams={filters}
                    columns={[
                        {
                            type: 'avatar',
                            value: (row: TeacherRowData) =>
                                row.img?.url ? row.img.url : null,
                            onClick: handleAvatarClick,
                        },
                        {
                            field: 'name',
                            title: t('Name'),
                            align: 'left',
                            value: (row: TeacherRowData) => (
                                <Box>
                                    <Link
                                        component="button"
                                        variant="body2"
                                        onClick={() =>
                                            setTeacherIdAndName({
                                                id: row.id,
                                                name:
                                                    row.fullName ||
                                                    t('Teacher'),
                                            })
                                        }
                                        sx={{
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            color: 'var(--brand-text)',
                                            textDecoration: 'none',
                                            fontWeight: 'bold',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {row.fullName || t('UserWithoutName')}
                                    </Link>
                                    {row.login && (
                                        <Box sx={{ mt: 0.5 }}>
                                            <Chip
                                                icon={<SchoolIcon />}
                                                label={`@${row.login}`}
                                                size="small"
                                                sx={{ fontSize: '0.75rem' }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            ),
                        },
                        {
                            field: 'contact',
                            title: t('Contact'),
                            align: 'center',
                            value: (row: TeacherRowData) => (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 0.5,
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Tooltip title={row.email || t('NoEmail')}>
                                        <EmailIcon
                                            fontSize="small"
                                            sx={{
                                                color:
                                                    row.email !== null &&
                                                    row.email !== ''
                                                        ? 'var(--brand-text)'
                                                        : 'action.disabled',
                                            }}
                                        />
                                    </Tooltip>
                                    <Tooltip title={row.phone || t('NoPhone')}>
                                        <PhoneAndroidIcon
                                            fontSize="small"
                                            sx={{
                                                color:
                                                    row.phone !== null &&
                                                    row.phone !== ''
                                                        ? 'success.main'
                                                        : 'action.disabled',
                                            }}
                                        />
                                    </Tooltip>
                                </Box>
                            ),
                        },
                        {
                            title: t('Email'),
                            field: 'email',
                            align: 'left',
                            value: (row: TeacherRowData) =>
                                row.email ? (
                                    <Link
                                        href={`mailto:${row.email}`}
                                        sx={{
                                            color: 'var(--brand-text)',
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {row.email}
                                    </Link>
                                ) : (
                                    '-'
                                ),
                        },
                        {
                            title: t('Phone'),
                            field: 'phone',
                            align: 'left',
                            value: (row: TeacherRowData) =>
                                row.phone ? (
                                    <Link
                                        href={`tel:${row.phone}`}
                                        sx={{
                                            color: 'var(--brand-text)',
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline',
                                            },
                                        }}
                                    >
                                        {row.phone}
                                    </Link>
                                ) : (
                                    '-'
                                ),
                        },
                        {
                            title: t('DNI'),
                            field: 'dni',
                            align: 'left',
                        },
                        {
                            title: t('Classes'),
                            field: 'numClasses',
                            type: 'counter',
                            align: 'right',
                        },
                    ]}
                />

                <SttButtonFab
                    onClick={goCreateNew}
                    permission={PERMISSIONS.ACTIVITY.MODIFY}
                />
            </SttPage>

            {teacherIdAndName && (
                <Teacher
                    teacherId={teacherIdAndName.id}
                    onClose={() => {
                        setTeacherIdAndName(null);
                        queryClient.invalidateQueries({
                            queryKey: [SPORTCENTER_ACTIVITY_TEACHERS],
                        });
                    }}
                    title={teacherIdAndName.name}
                />
            )}

            <PhotoViewerDialog
                open={photoViewerOpen}
                onClose={() => {
                    setPhotoViewerOpen(false);
                    setSelectedPhotoUrl(null);
                }}
                photoUrl={selectedPhotoUrl}
                altText={t('teacherPhoto')}
            />
        </>
    );
}
